import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { Manager, Reference, Popper } from '@atlaskit/popper';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ColumnPickerContainer,
	useColumnPickerOpenState,
} from '@atlassian/jira-issue-table-column-picker/src/controllers/column-picker-open-state/index.tsx';
import { useVisibleDepth } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import type { Props as ColumnPickerProps } from '@atlassian/jira-issue-table-column-picker/src/ui/ColumnConfigWithContainer.tsx';
import { COLUMN_ID_MERGED_COLUMN } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import { useInlineFieldCreateOnboardingContext } from '../../inline-field-create-onboarding/index.tsx';
import { getExpandColumnWidth } from '../utils.tsx';
import {
	useInlineCreateActiveIndexActions,
	useInlineFieldCreateActiveIndex,
} from '../../../controllers/inline-create-active-index/index.tsx';
import { useScrollStateSelector } from '../../../controllers/scroll-state/index.tsx';
import { FIELD } from '../../../controllers/inline-create-active-index/types.tsx';
import {
	COLUMN_ID_EXPAND_BUTTON,
	COLUMN_ID_CHECKBOX,
	INSERT_COLUMN_BUTTON_Y_OFFSET,
	COLUMN_ID_INTERNAL_COLUMN,
	COLUMN_PICKER_ADD_COLUMN_CONTAINER,
	MINIMUM_MERGED_CELL_COLUMN_WIDTH,
	MINIMUM_MERGED_CELL_COLUMN_WIDTH_TIMELINE,
} from '../../../common/constants.tsx';
import type { Column } from '../../../common/types.tsx';
import {
	useIsInlineFieldConfigEnabled,
	useIsMergedColumnEnabled,
	useProjectContext,
	useIsTimelineEnabled,
	useIsInBetweenStickyColumnPickerEnabled,
} from '../../../controllers/features/selectors.tsx';
import { IFCFooter } from '../../../common/ui/inline-field-config/column-picker-footer/index.tsx';
import type { InsertColumnButton$key } from './__generated__/InsertColumnButton.graphql';

export type InsertColumnButtonProps = {
	view?: InsertColumnButton$key | null;
	columns: Column[];
	columnPickerProps?: ColumnPickerProps;
	isStickyColumnsVisible?: boolean;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	mergedColumnMinWidth?: number;
};

// Height/width of the popup button
const DEFAULT_BUTTON_SIZE = 24;
// Height/width of the reference element the popup button is anchored to
const REFERENCE_SIZE = 1;

const constantModifiers = [
	{
		name: 'flip',
		enabled: false,
	},
];

const ColumnPicker = importCond<
	typeof import('@atlassian/jira-issue-table-column-picker/src/ui/ColumnConfigWithContainer.tsx'),
	typeof import('@atlassian/jira-issue-table-column-picker/src/ui/async.tsx')
>(
	'jira_timeline_sticky_assignee_status_columns',
	'@atlassian/jira-issue-table-column-picker/src/ui/ColumnConfigWithContainer.tsx',
	'@atlassian/jira-issue-table-column-picker/src/ui/async.tsx',
);

export const InsertColumnButtonInner = ({
	view,
	columns,
	columnPickerProps,
	isStickyColumnsVisible,
	mergedColumnMinWidth,
}: InsertColumnButtonProps) => {
	const [{ index: columnIndex }] = useInlineFieldCreateActiveIndex();
	const [{ isOpen }, { close }] = useColumnPickerOpenState();
	const { lockIndex, clearIndex, clearIndexAfterDelay, setIndex, setIsHovered, clearIsHovered } =
		useInlineCreateActiveIndexActions();
	const { showInlineFieldCreateOnboarding } = useInlineFieldCreateOnboardingContext();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isInBetweenStickyColumnPickerEnabled = useIsInBetweenStickyColumnPickerEnabled();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const projectContext = fg('jira_inline_field_config_gate') ? useProjectContext() : undefined;

	const viewData = useFragment<InsertColumnButton$key>(
		graphql`
			fragment InsertColumnButton on JiraIssueSearchViewMetadata {
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...ColumnConfigWithContainer_savedViewId
			}
		`,
		view,
	);

	const visibleDepth = useVisibleDepth();

	let isMergedColumnEnabled = false;
	if (fg('jira_list_merged_column_gate')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isMergedColumnEnabled = useIsMergedColumnEnabled();
	}

	const firstFieldColumnIndex = useMemo(() => {
		return columns.findIndex((column) => !column.id.startsWith(COLUMN_ID_INTERNAL_COLUMN)) - 1;
	}, [columns]);

	const hasShownOnboarding = useRef(false);

	const shouldShowOnboarding = showInlineFieldCreateOnboarding && !hasShownOnboarding.current;

	const isTimelineEnabled = useIsTimelineEnabled();

	if (shouldShowOnboarding) {
		setIndex(FIELD, firstFieldColumnIndex);
		lockIndex(FIELD);
		hasShownOnboarding.current = true;
	}

	const columnsWidth = useMemo(
		() =>
			columns.map((column) => {
				if (column.id === COLUMN_ID_EXPAND_BUTTON) {
					return getExpandColumnWidth(visibleDepth);
				}
				if (
					column.id === COLUMN_ID_MERGED_COLUMN &&
					column.mergedColumns &&
					column.mergedColumns.length > 1
				) {
					if (fg('refactor_issue_table_for_timline')) {
						return Math.max(
							mergedColumnMinWidth ?? MINIMUM_MERGED_CELL_COLUMN_WIDTH,
							column.customWidth ?? column.defaultWidth,
						);
					}
					return Math.max(
						isTimelineEnabled
							? MINIMUM_MERGED_CELL_COLUMN_WIDTH_TIMELINE
							: MINIMUM_MERGED_CELL_COLUMN_WIDTH,
						column.customWidth ?? column.defaultWidth,
					);
				}
				return column.customWidth ?? column.defaultWidth;
			}),
		[columns, isTimelineEnabled, mergedColumnMinWidth, visibleDepth],
	);

	const fixedColumnCount = useMemo(
		() =>
			columns.filter(
				(column) =>
					column.id === COLUMN_ID_EXPAND_BUTTON ||
					column.id === COLUMN_ID_CHECKBOX ||
					column.id === COLUMN_ID_MERGED_COLUMN ||
					(isStickyColumnsVisible &&
						column.isColumnSticky &&
						(!fg('jira_timeline_sticky_assignee_status_columns') ||
							!isInBetweenStickyColumnPickerEnabled) &&
						fg('issue_table_frozen_columns')),
			).length,
		[columns, isStickyColumnsVisible, isInBetweenStickyColumnPickerEnabled],
	);

	const mergedColumnsCount =
		isMergedColumnEnabled && fg('jira_list_merged_column_gate')
			? (columns.find((column) => column.id === COLUMN_ID_MERGED_COLUMN)?.mergedColumns ?? [])
					.length
			: 0;

	const isEmptyColumnExist = columnsWidth.length - columns.length === 1;

	const isStickyInsertButtonApplied = fg('jira_timeline_sticky_assignee_status_columns')
		? isInBetweenStickyColumnPickerEnabled &&
			isStickyColumnsVisible &&
			columnIndex != null &&
			columns[columnIndex]?.isColumnSticky &&
			fg('issue_table_frozen_columns')
		: false;

	let scrollElement = null;

	if (fg('jira_timeline_sticky_assignee_status_columns')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		scrollElement = useScrollStateSelector((state) => state.scrollElement);
	}

	const referenceRef = useRef<HTMLDivElement | null>(null);

	const insertButtonXOffset = useMemo(() => {
		if (columnIndex != null && columnsWidth.length > 0) {
			return columnsWidth
				.slice(0, columnIndex + 1)
				.reduce((acc, width) => acc + width, -REFERENCE_SIZE);
		}
		return 0;
	}, [columnIndex, columnsWidth]);

	// Update position of + button on sticky columns using CSS variable on scroll - this will not trigger re renders
	useEffect(() => {
		if (!isStickyInsertButtonApplied || !scrollElement || !referenceRef.current) {
			return;
		}

		const updatePosition = () => {
			if (referenceRef.current && scrollElement) {
				// Update CSS variable directly - this will not trigger a re-render
				referenceRef.current.style.setProperty('--scroll-offset', `${scrollElement.scrollLeft}px`);
			}
		};

		updatePosition();

		scrollElement.addEventListener('scroll', updatePosition, { passive: true });

		return () => {
			scrollElement.removeEventListener('scroll', updatePosition);
		};
	}, [isStickyInsertButtonApplied, scrollElement]);

	const finalOffset: [number, number] = useMemo(
		() =>
			// Negatively offset the popup by the button + reference size to center the button on the secondary axis
			[
				insertButtonXOffset,
				INSERT_COLUMN_BUTTON_Y_OFFSET - (DEFAULT_BUTTON_SIZE + REFERENCE_SIZE) / 2,
			],
		[insertButtonXOffset],
	);

	const modifiers = useMemo(() => {
		// Adjust the overflow calculation for the primary axis so it is relative to the center of the button.
		const alongCenterOffset = -(DEFAULT_BUTTON_SIZE / 2);
		// Retain the provided offset for the secondary axis
		const padding = { top: finalOffset[1], left: alongCenterOffset, right: alongCenterOffset };
		return [
			...constantModifiers,
			{
				name: 'preventOverflow',
				options: {
					mainAxis: true,
					altAxis: true,
					padding,
					altBoundary: true,
					rootBoundary: 'viewport',
					tether: false,
				},
			},
			{
				name: 'eventListeners',
				options: {
					// Only recompute position on scroll/resize events while the button is focused to reduce CPU load
					scroll: isOpen,
					resize: isOpen,
				},
			},
		];
	}, [finalOffset, isOpen]);

	const handleMouseEnter = useCallback(() => {
		lockIndex(FIELD);
	}, [lockIndex]);

	const handleMouseLeave = useCallback(() => {
		!isOpen && clearIndexAfterDelay(FIELD);
	}, [clearIndexAfterDelay, isOpen]);

	const handlePickerMouseEnter = useCallback(() => {
		setIsHovered(FIELD);
	}, [setIsHovered]);

	const handlePickerMouseLeave = useCallback(() => {
		clearIsHovered(FIELD);
	}, [clearIsHovered]);

	const shouldNotShowInsertButton = useMemo(
		() =>
			columnIndex == null ||
			(isEmptyColumnExist && columnIndex >= columnsWidth.length - 3) || // If an empty column exists, prevent inserting in the last two columns.
			(!(fg('jira_timeline_sticky_assignee_status_columns') && isStickyInsertButtonApplied) &&
				columnIndex >= columnsWidth.length - 2) || // Prevent inserting on the left or right of the last column unless isStickyInsertButtonApplied.
			columnIndex < fixedColumnCount - 1,
		[
			columnIndex,
			columnsWidth.length,
			fixedColumnCount,
			isEmptyColumnExist,
			isStickyInsertButtonApplied,
		],
	);

	if (shouldNotShowInsertButton) {
		if (!shouldShowOnboarding) {
			clearIndex(FIELD);
			close();
		}
	}

	if (columnPickerProps) {
		const { components, ...props } = columnPickerProps;
		const emptyFooter = fg('slv-117-nin-column-picker-style-updates')
			? {}
			: { Footer: () => <></> };
		return (
			<Manager>
				<Reference>
					{({ ref }) => {
						const handleRef = (node: HTMLDivElement | null) => {
							if (typeof ref === 'function') {
								ref(node);
							}
							if (isStickyInsertButtonApplied) {
								referenceRef.current = node;
							}
						};

						return (
							<Box
								xcss={[
									referenceStyles,
									visuallyHiddenStyles,
									...(isStickyInsertButtonApplied ? [stickyStyles] : []),
								]}
								ref={fg('jira_timeline_sticky_assignee_status_columns') ? handleRef : ref}
							/>
						);
					}}
				</Reference>
				<Popper placement="bottom" offset={finalOffset} modifiers={modifiers}>
					{({ ref, style }) => {
						const styleProps = { style };
						return (
							<Box
								ref={ref}
								// eslint-disable-next-line react/jsx-props-no-spreading
								{...styleProps}
								onMouseEnter={handleMouseEnter}
								onMouseLeave={handleMouseLeave}
								xcss={[
									buttonStyles,
									shouldNotShowInsertButton && hideStyles,
									...(isStickyInsertButtonApplied ? [stickyPopperStyles] : []),
								]}
								backgroundColor="color.background.input"
								testId={`native-issue-table.ui.issue-table.insert-column-button.popper${shouldNotShowInsertButton ? '-hidden' : ''}`}
							>
								<ColumnPicker
									{...props}
									view={viewData}
									components={{
										...components,
										...(isInlineFieldConfigEnabled ? { Footer: IFCFooter } : emptyFooter),
									}}
									columnInsertIndex={
										columnIndex != null
											? columnIndex - fixedColumnCount + mergedColumnsCount + 1
											: undefined
									}
									isInBetweenColumnPickerEnabled
									showInlineFieldCreateOnboarding={
										columnIndex === firstFieldColumnIndex && showInlineFieldCreateOnboarding
									}
									onPickerMouseEnter={handlePickerMouseEnter}
									onPickerMouseLeave={handlePickerMouseLeave}
									isMergedColumnEnabled={isMergedColumnEnabled}
									isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
									projectContext={
										projectContext !== null && fg('jira_inline_field_config_gate')
											? projectContext
											: undefined
									}
								/>
							</Box>
						);
					}}
				</Popper>
			</Manager>
		);
	}
	return null;
};

export const InsertColumnButton = (props: InsertColumnButtonProps) => {
	return (
		<ColumnPickerContainer scope={COLUMN_PICKER_ADD_COLUMN_CONTAINER}>
			<InsertColumnButtonInner {...props} />
		</ColumnPickerContainer>
	);
};

const buttonStyles = xcss({
	zIndex: 'dialog',
});
const referenceStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: token('space.0'),
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: `${DEFAULT_BUTTON_SIZE / 2 + 2}px`,
});
const hideStyles = xcss({
	display: 'none',
});

const stickyStyles = xcss({
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	left: token('space.0'),
	transform: 'translateX(var(--scroll-offset, 0px))',
	willChange: 'transform',
});

const stickyPopperStyles = xcss({
	borderRadius: token('radius.small'),
});

const visuallyHiddenStyles = xcss({
	width: '1px',
	height: '1px',
	padding: 'space.0',
	position: 'absolute',
	border: 'none',
	clipPath: 'rect(1px 1px 1px 1px)',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});
